import { ConfirmationBox, Icon, IconName } from '@infotech-soe/erp-ui-kit';
import React, { memo, useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationMessageType } from '../../../services/commands/UserInteractor';
import DatasetService from '../../../services/dataset/DatasetService';
import { getCaptionText } from '../Caption';

import { CommandConfirmationDialogProps } from './CommandConfirmationDialogContainer';
import { useStyles } from './styles';

type Props = Omit<CommandConfirmationDialogProps, 'type'>;

const messages = defineMessages({
  title: {
    id: 'core.confirmation.title',
    defaultMessage: 'Confirm action',
  },
  [ConfirmationMessageType.GENERIC]: {
    id: 'core.confirmation.generic',
    defaultMessage: 'Perform the selected action "{commandName}" ?',
  },
  [ConfirmationMessageType.PRINT]: {
    id: 'core.confirmation.print',
    defaultMessage: 'Send the task to print server?',
  },
  [`${ConfirmationMessageType.GENERIC}Long`]: {
    id: 'core.confirmation.generic.long',
    defaultMessage: 'This action cannot be undone.',
  },
  [`${ConfirmationMessageType.PRINT}Long`]: {
    id: 'core.confirmation.print.long',
    defaultMessage: 'After print complete you will receive a confirmation.',
  },
  [ConfirmationMessageType.DELETE]: {
    id: 'core.confirmation.delete',
    defaultMessage: 'Delete the record?',
  },
  [`${ConfirmationMessageType.DELETE}Long`]: {
    id: 'core.confirmation.delete.long',
    defaultMessage: 'This will delete the selected record completely.',
  },
});

const DEFAULT_ICONS = new Map<ConfirmationMessageType, IconName>([
  [ConfirmationMessageType.GENERIC, 'question'],
  [ConfirmationMessageType.PRINT, 'print'],
  [ConfirmationMessageType.DELETE, 'delete'],
]);

const DatasetConfirmationDialog = (props: Props) => {
  const {
    open,
    messageType = ConfirmationMessageType.GENERIC,
    commandCaption,
    dataset,
    icon,
    shortMessage,
    longMessage,
    onCancel,
    onConfirm,
  } = props;
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const commandName = useMemo(
    () => commandCaption && getCaptionText(commandCaption, formatMessage),
    [commandCaption, formatMessage]
  );

  const modalTrigger = useCallback(
    () => ({
      focus: () => {
        if (dataset) {
          new DatasetService().focus(dataset);
        }
      },
    }),
    [dataset]
  );

  return (
    <ConfirmationBox
      className={classes.dialog}
      height="auto"
      modalTrigger={modalTrigger}
      open={open}
      title={formatMessage(messages.title)}
      width="auto"
      onCancel={onCancel}
      onClose={onCancel}
      onConfirm={() => onConfirm(messageType)}
    >
      <div className={classes.container}>
        <div className={classes.header}>
          <div className={classes.icon}>
            <Icon name={icon || DEFAULT_ICONS.get(messageType)} size="3em" />
          </div>
          <div className={classes.shortMessage}>
            {shortMessage ||
              formatMessage(messages[messageType], { commandName })}
          </div>
        </div>
        <div className={classes.longMessage}>
          {longMessage || formatMessage(messages[`${messageType}Long`])}
        </div>
      </div>
    </ConfirmationBox>
  );
};

export default memo(DatasetConfirmationDialog);
